// PeriodNavigator: Navigation bar for the agenda list view
// Handles week/month sub-toggle, prev/next navigation, and Today shortcut
// CREATED: Part of the agenda list view for TeamView
// UPDATED: Use focus-visible instead of focus for ring (no green outline on mouse click)
// FIXED: getWeekEnd and month end now set 23:59:59.999 so isCurrentPeriod includes the full last day
// FIXED: Layout uses flex-wrap + shrinkable label to prevent overflow on narrow viewports

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useCallback } from 'react';

export type PeriodType = 'week' | 'month';

interface PeriodNavigatorProps {
  periodType: PeriodType;
  referenceDate: Date;
  onNavigate: (newDate: Date) => void;
  onPeriodTypeChange: (type: PeriodType) => void;
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekEnd(date: Date): Date {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

export function getPeriodBounds(referenceDate: Date, periodType: PeriodType): { start: Date; end: Date } {
  if (periodType === 'week') {
    return { start: getWeekStart(referenceDate), end: getWeekEnd(referenceDate) };
  }
  const start = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const end = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function isCurrentPeriod(referenceDate: Date, periodType: PeriodType): boolean {
  const today = new Date();
  if (periodType === 'week') {
    const { start, end } = getPeriodBounds(referenceDate, 'week');
    return today >= start && today <= end;
  }
  return today.getFullYear() === referenceDate.getFullYear() &&
    today.getMonth() === referenceDate.getMonth();
}

export function formatPeriodLabel(referenceDate: Date, periodType: PeriodType): string {
  if (periodType === 'month') {
    const monthName = referenceDate.toLocaleDateString('fr-FR', { month: 'long' });
    const year = referenceDate.getFullYear();
    return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
  }
  const { start, end } = getPeriodBounds(referenceDate, 'week');
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleDateString('fr-FR', { month: 'short' });
  const endMonth = end.toLocaleDateString('fr-FR', { month: 'short' });
  const year = end.getFullYear();
  if (start.getMonth() === end.getMonth()) {
    return `Sem. du ${startDay} au ${endDay} ${endMonth} ${year}`;
  }
  return `Sem. du ${startDay} ${startMonth} au ${endDay} ${endMonth} ${year}`;
}

export function PeriodNavigator({ periodType, referenceDate, onNavigate, onPeriodTypeChange }: PeriodNavigatorProps) {
  const label = useMemo(() => formatPeriodLabel(referenceDate, periodType), [referenceDate, periodType]);
  const showToday = !isCurrentPeriod(referenceDate, periodType);

  const handlePrev = useCallback(() => {
    const d = new Date(referenceDate);
    if (periodType === 'week') {
      d.setDate(d.getDate() - 7);
    } else {
      d.setDate(1);
      d.setMonth(d.getMonth() - 1);
    }
    onNavigate(d);
  }, [referenceDate, periodType, onNavigate]);

  const handleNext = useCallback(() => {
    const d = new Date(referenceDate);
    if (periodType === 'week') {
      d.setDate(d.getDate() + 7);
    } else {
      d.setDate(1);
      d.setMonth(d.getMonth() + 1);
    }
    onNavigate(d);
  }, [referenceDate, periodType, onNavigate]);

  const handleToday = useCallback(() => {
    onNavigate(new Date());
  }, [onNavigate]);

  return (
    <div className="flex items-center justify-between gap-3 py-3 px-1 flex-shrink-0 flex-wrap">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <button
          type="button"
          onClick={handlePrev}
          className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
          aria-label={periodType === 'week' ? 'Semaine précédente' : 'Mois précédent'}
        >
          <ChevronLeft size={18} />
        </button>

        <span className="text-sm font-semibold text-gray-800 min-w-0 truncate text-center select-none">
          {label}
        </span>

        <button
          type="button"
          onClick={handleNext}
          className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
          aria-label={periodType === 'week' ? 'Semaine suivante' : 'Mois suivant'}
        >
          <ChevronRight size={18} />
        </button>

        {showToday && (
          <button
            type="button"
            onClick={handleToday}
            className="px-2.5 py-1 text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
          >
            Aujourd'hui
          </button>
        )}
      </div>

      <div className="flex items-center bg-gray-100 rounded-lg p-0.5" role="group" aria-label="Période">
        <button
          type="button"
          onClick={() => onPeriodTypeChange('week')}
          aria-pressed={periodType === 'week'}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ${
            periodType === 'week'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Semaine
        </button>
        <button
          type="button"
          onClick={() => onPeriodTypeChange('month')}
          aria-pressed={periodType === 'month'}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ${
            periodType === 'month'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Mois
        </button>
      </div>
    </div>
  );
}
